import { useState } from 'react';
import { css, jsx } from '@emotion/react';
import { AlphabeticAppNav } from '../AlphabeticAppNav';
import { Branding } from '../Branding';
import { HelpfulLinks, HelpfulLinksCssVars } from '../HelpfulLinks';
import { SocialIcons } from '../SocialIcons';
import { ZapierMeta, ZapierMetaCssVars } from '../ZapierMeta';
import { LinksSection } from '../LinksSection';
import { useQueryFooterData } from '../../hooks/useQueryFooterData';
import { boxSizing } from '../../../utils/style-mixins/reset';
const mediaQueryMedium = '(min-width: 650px)';
const mediaQueryLarge = '(min-width: 980px)';
const mediaQueryTablet = '(max-width: 980px)';
const Styles = {
    root: css `
    ${boxSizing};
    background-color: var(--zds-background-weaker, #fffdf9);
    padding: 30px;
    @media ${mediaQueryTablet} {
      padding: 20px;
    }
  `,
    simplifiedNav: css `
    ${boxSizing};
    display: flex;
    justify-content: center;
    justify-items: center;
  `,
    grid: (props) => css `
    ${boxSizing};
    display: grid;
    grid-template-rows: auto;
    grid-template-columns: 100%;
    grid-template-areas:
      ${props.shouldRenderAlphabetNav ? '"alphabetNav"' : ''}
      ${props.shouldRenderSeoLinks ? '"seoLinks"' : ''}
      "helpfulLinks"
      "socialIcons"
      "branding"
      "zapierMeta";
    justify-items: center;
    align-items: center;
    gap: 30px;
    max-width: 1200px;
    margin: 0 auto;

    @media ${mediaQueryMedium} {
      grid-template-columns: max-content 1fr;
      grid-template-areas:
        ${props.shouldRenderAlphabetNav ? '"alphabetNav alphabetNav"' : ''}
        ${props.shouldRenderSeoLinks ? '"seoLinks seoLinks"' : ''}
        "helpfulLinks helpfulLinks"
        "socialIcons socialIcons"
        "branding zapierMeta";
    }

    @media ${mediaQueryLarge} {
      grid-template-columns: max-content 1fr;
      grid-template-areas:
        ${props.shouldRenderAlphabetNav ? '"alphabetNav alphabetNav"' : ''}
        ${props.shouldRenderSeoLinks ? '"seoLinks seoLinks"' : ''}
        "socialIcons helpfulLinks"
        "branding zapierMeta";
    }
  `,
    branding: css `
    ${boxSizing};
    grid-area: branding;
    @media ${mediaQueryMedium} {
      justify-self: start;
    }
  `,
    helpfulLinks: css `
    ${boxSizing};
    grid-area: helpfulLinks;
    width: 100%;
    display: flex;
    justify-content: center;
    @media ${mediaQueryMedium} {
      ${HelpfulLinksCssVars.flexDirection}: row;
    }
    @media ${mediaQueryLarge} {
      justify-content: flex-end;
      ${HelpfulLinksCssVars.justifyContent}: flex-end;
    }
  `,
    alphabetNav: css `
    ${boxSizing};
    grid-area: alphabetNav;

    @media ${mediaQueryMedium} {
      max-width: none;
    }
  `,
    seoLinks: css `
    ${boxSizing};
    grid-area: seoLinks;
    width: 100%;
  `,
    socialIcons: css `
    ${boxSizing};
    grid-area: socialIcons;
    @media ${mediaQueryLarge} {
      justify-self: start;
    }
  `,
    zapierMeta: css `
    ${boxSizing};
    grid-area: zapierMeta;
    @media ${mediaQueryMedium} {
      justify-self: end;
      ${ZapierMetaCssVars.justifyContent}: flex-end;
    }
  `,
    linksSections: css `
    ${boxSizing};
    display: grid;
    grid-template-columns: 100%;
    text-align: center;
    column-gap: 10px;
    row-gap: 20px;
    @media (min-width: 420px) {
      text-align: left;
      grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    }
  `,
};
const LinksSections = (props) => {
    const [areSectionsExpanded, setAreSectionsExpanded] = useState(false);
    const toggleAreSectionsExpanded = () => {
        setAreSectionsExpanded(!areSectionsExpanded);
    };
    return (jsx("div", { css: Styles.linksSections, "data-zds": true }, props.sections.map((section) => (jsx(LinksSection, { key: section.title + areSectionsExpanded, title: section.title, links: section.links, isExpanded: areSectionsExpanded, onToggleExpanded: toggleAreSectionsExpanded })))));
};
export const UniversalFooter = (props) => {
    const alphabetNav = props.shouldRenderAlphabetNav && (jsx("div", { css: Styles.alphabetNav, "data-zds": true },
        jsx(AlphabeticAppNav, null)));
    const { data: footerData } = useQueryFooterData();
    const seoLinks = props.shouldRenderSeoLinks && (jsx("div", { css: Styles.seoLinks, "data-zds": true },
        jsx(LinksSections, { sections: [
                footerData.seoLinks.topSearches,
                footerData.seoLinks.popularApps,
                footerData.seoLinks.trendingApps,
                footerData.seoLinks.topAppsByCategory,
                footerData.seoLinks.ourBestContent,
            ] })));
    return (jsx("footer", { css: Styles.root, role: "contentinfo", "data-testid": "universalFooter", "data-zds": true }, props.showSimplifiedFooter ? (jsx("div", { css: Styles.simplifiedNav, "data-zds": true },
        jsx(ZapierMeta, { links: [
                ...footerData.metaLinks,
                {
                    text: 'Manage cookies',
                    className: 'ot-sdk-show-settings',
                    style: { display: 'none' },
                },
            ] }))) : (jsx("div", { css: Styles.grid(props), "data-zds": true },
        jsx("div", { css: Styles.helpfulLinks, "data-zds": true },
            jsx(HelpfulLinks, { links: footerData.helpfulLinks })),
        alphabetNav,
        seoLinks,
        jsx("div", { css: Styles.socialIcons, "data-zds": true },
            jsx(SocialIcons, { icons: footerData.socialIcons })),
        jsx("div", { css: Styles.branding, "data-zds": true },
            jsx(Branding, null)),
        jsx("div", { css: Styles.zapierMeta, "data-zds": true },
            jsx(ZapierMeta, null))))));
};
//# sourceMappingURL=UniversalFooter.js.map
// @zapier/universal-layout@11.0.68 (90b2cf70c)
